import { alpha, keyframes, styled, Table as MuiTable, TableContainer } from '@mui/material';

const shimmer = keyframes({
  '0%': { backgroundPosition: '-200% 0' },
  '100%': { backgroundPosition: '200% 0' },
});

export const Wrapper = styled(TableContainer, {
  shouldForwardProp: (prop) => prop !== 'mobileApp' && prop !== 'tableHeight' && prop !== 'fixedHeight' && prop !== 'isHeliosTable',
})<{ mobileApp?: boolean; tableHeight: string; fixedHeight?: boolean; isHeliosTable?: boolean }>(
  ({ theme, mobileApp, tableHeight, fixedHeight, isHeliosTable }) => ({
    width: '100%',
    minHeight: '100%',
    height: fixedHeight ? tableHeight : '100%',
    maxHeight: tableHeight,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'start',
    backgroundColor: theme.palette.common.white,
    overflowX: 'auto',
    overflowY: fixedHeight ? 'auto' : undefined,
    borderRadius: mobileApp ? (isHeliosTable ? '0 0 8px 8px' : '8px') : 0,
    '& > .table-overflow': {
      width: '100%',
      overflowX: 'auto',
    },
  })
);

export const StyledTable = styled(MuiTable)(() => ({
  minWidth: '100%',
  width: '100%',
  height: '100%',
}));

export const TableControl = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isPinnedDashboard' && prop !== 'mobileApp',
})<{ isPinnedDashboard?: boolean; mobileApp?: boolean }>(({ theme, isPinnedDashboard, mobileApp }) => ({
  width: '100%',
  padding: isPinnedDashboard ? '0' : '12px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  ...(mobileApp
    ? {
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.fab,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }
    : {}),
  [theme.breakpoints.down('sm')]: {
    padding: isPinnedDashboard ? '0' : '8px 12px',
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

export const ControlButtonsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '8px',
  '& .MuiButton-outlined': {
    borderRadius: '8px',
    borderColor: theme.palette.grey[300],
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontSize: '13px',
    fontWeight: 500,
    padding: '4px 12px',
    minHeight: '32px',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: 'transparent',
    },
  },
  '& .MuiIconButton-root': {
    borderRadius: '8px',
  },
}));

export const headerIconButtonStyles = {
  border: '1px solid',
  borderColor: 'grey.300',
  borderRadius: '8px',
  p: '6px',
  color: 'text.secondary',
  '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
} as const;

export const TableCardWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isLoading' && prop !== 'padded' && prop !== 'overflowVisible',
})<{ isLoading?: boolean; padded?: boolean; overflowVisible?: boolean }>(({ theme, isLoading, padded, overflowVisible }) => ({
  width: '100%',
  height: '100%',
  minHeight: isLoading ? '400px' : 'auto',
  borderRadius: '8px',
  overflow: overflowVisible ? 'visible' : 'hidden',
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.background.paper,
  paddingBottom: '8px',
  ...(padded
    ? {
        padding: '16px 20px',
        paddingBottom: '16px',
      }
    : {}),
}));

export const NoDataWrapper = styled('div')(() => ({
  width: '100%',
  minHeight: '128px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px',
}));

export const TableSkeleton = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
  gap: '0',
  width: '100%',
  '& > div': {
    padding: '0 16px',
    boxSizing: 'border-box',
  },
}));

export const SkeletonRow = styled('div')(() => ({
  display: 'flex',
  gap: '16px',
  height: '44px',
  alignItems: 'center',
  borderBottom: '1px solid',
  borderBottomColor: 'rgba(0,0,0,0.04)',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const SkeletonCell = styled('div')(({ theme }) => ({
  height: '16px',
  borderRadius: '4px',
  flex: 1,
  backgroundImage: `linear-gradient(90deg, ${alpha(theme.palette.text.primary, 0.06)} 0%, ${alpha(theme.palette.text.primary, 0.12)} 50%, ${alpha(theme.palette.text.primary, 0.06)} 100%)`,
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
  '&:first-of-type': {
    flex: '0 0 32px',
  },
  '&:nth-of-type(2)': {
    flex: '0 0 120px',
  },
}));
